import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { hsnCodeService, HSNCode } from "../../services/hsn-code.service";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export function HSNCodesPage() {
  const navigate = useNavigate();
  const [hsnCodes, setHsnCodes] = useState<HSNCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [gstFilter, setGstFilter] = useState<string>("all");
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    code: string;
  }>({ isOpen: false, id: "", code: "" });
  const [seedModal, setSeedModal] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const limit = 20;

  useEffect(() => {
    loadHSNCodes();
  }, [page, gstFilter]);

  const loadHSNCodes = async () => {
    try {
      setLoading(true);
      const response = await hsnCodeService.getAll({
        page,
        limit,
        search: searchTerm || undefined,
        gstRate: gstFilter !== "all" ? parseFloat(gstFilter) : undefined,
      });
      setHsnCodes(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load HSN codes",
      );
      setHsnCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadHSNCodes();
  };

  const handleSeedDefaultsClick = () => {
    setSeedModal(true);
  };

  const handleSeedDefaultsConfirm = async () => {
    setSeeding(true);
    try {
      const result = await hsnCodeService.seedDefaults();
      showToast.success(
        result.message || `Created ${result.created} HSN codes`,
      );
      loadHSNCodes();
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to seed HSN codes",
      );
    } finally {
      setSeeding(false);
      setSeedModal(false);
    }
  };

  const handleDeleteClick = (id: string, code: string) => {
    setDeleteModal({ isOpen: true, id, code });
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await hsnCodeService.delete(deleteModal.id);
      showToast.success("HSN code deleted successfully");
      setDeleteModal({ isOpen: false, id: "", code: "" });
      loadHSNCodes();
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to delete HSN code",
      );
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HSN Codes</h1>
          <p className="text-sm text-gray-500 mt-1">
            {total} HSN code{total !== 1 ? "s" : ""} configured
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSeedDefaultsClick}
            className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
          >
            Seed Defaults
          </button>
          <button
            onClick={() => navigate("/hsn-codes/create")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Add HSN Code
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search by HSN code or description..."
            className="flex-1 min-w-[200px] px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={gstFilter}
            onChange={(e) => {
              setGstFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All GST Rates</option>
            <option value="0">0% GST</option>
            <option value="5">5% GST</option>
            <option value="12">12% GST</option>
            <option value="18">18% GST</option>
            <option value="28">28% GST</option>
          </select>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : hsnCodes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No HSN codes found. Click "Seed Defaults" to create standard pharma
            HSN codes.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  HSN Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  GST Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CGST / SGST
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hsnCodes.map((hsn) => (
                <tr key={hsn.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono font-medium text-gray-900">
                      {hsn.hsnCode}
                    </div>
                    {hsn.chapter && (
                      <div className="text-xs text-gray-500">
                        Ch. {hsn.chapter}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-md truncate">
                      {hsn.hsnDescription}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded">
                      {hsn.gstRate}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {hsn.cgstRate ?? hsn.gstRate / 2}% /{" "}
                    {hsn.sgstRate ?? hsn.gstRate / 2}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {hsn._count?.products || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        hsn.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {hsn.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => navigate(`/hsn-codes/${hsn.id}/edit`)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(hsn.id, hsn.hsnCode)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t">
            <div className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: "", code: "" })}
        onConfirm={handleDeleteConfirm}
        title="Delete HSN Code"
        message={`Are you sure you want to delete HSN code "${deleteModal.code}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />

      {/* Seed Defaults Confirmation Modal */}
      <ConfirmDialog
        isOpen={seedModal}
        onClose={() => setSeedModal(false)}
        onConfirm={handleSeedDefaultsConfirm}
        title="Seed Default HSN Codes"
        message="This will create default HSN codes for pharma products. Continue?"
        confirmText="Seed Defaults"
        cancelText="Cancel"
        variant="info"
        isLoading={seeding}
      />
    </div>
  );
}

export default HSNCodesPage;
